/**
 * Server-Sent Events client with bearer-auth + auto-reconnect.
 *
 * The native `EventSource` API can't carry an Authorization header, so we use
 * `fetch` with `text/event-stream` and parse the wire format manually. The
 * GroupCart broker emits two flavours:
 *   - `event: snapshot\ndata: {...full cart...}` — initial sync
 *   - `event: <type>\ndata: {...delta...}`       — line.added, participant.joined, etc.
 *
 * Reconnect uses exponential back-off (500ms → 10s) and resets to 500ms after
 * a successful re-open. Aborting the returned closer cancels both the in-
 * flight fetch and any pending back-off sleep.
 */

export interface SSEHandlers {
  onSnapshot?: (data: unknown) => void;
  onEvent?: (type: string, data: unknown) => void;
  onError?: (err: unknown) => void;
  onOpen?: () => void;
}

export type SSECloser = () => void;

const INITIAL_BACKOFF_MS = 500;
const MAX_BACKOFF_MS = 10_000;

export interface SSEOpenOptions {
  /** Optional extra headers — e.g. `X-API-KEY` when the SSE endpoint
   *  bypasses the gateway and hits Cloud Run directly. */
  extraHeaders?: Record<string, string | null | undefined>;
}

export function openSSE(
  url: string,
  token: string,
  handlers: SSEHandlers,
  options: SSEOpenOptions = {},
): SSECloser {
  const ctrl = new AbortController();
  const signal = ctrl.signal;

  const sleep = (ms: number) =>
    new Promise<void>((resolve) => {
      const t = setTimeout(resolve, ms);
      signal.addEventListener(
        "abort",
        () => {
          clearTimeout(t);
          resolve();
        },
        { once: true },
      );
    });

  void (async () => {
    let backoffMs = INITIAL_BACKOFF_MS;

    while (!signal.aborted) {
      try {
        const headers: Record<string, string> = {
          // Use X-Participant-Token instead of Authorization so the API
          // Gateway doesn't try to validate the participant JWT as a
          // Firebase token (which would 401 before reaching the backend).
          "X-Participant-Token": `Bearer ${token}`,
          Accept: "text/event-stream",
        };
        for (const [k, v] of Object.entries(options.extraHeaders ?? {})) {
          if (v) headers[k] = v;
        }
        const res = await fetch(url, {
          headers,
          signal,
          // Keep credentials in line with the rest of the API surface so the
          // gateway sees the same auth context (cookies for refresh, etc.).
          credentials: "include",
        });

        if (!res.ok || !res.body) {
          throw new Error(`SSE HTTP ${res.status}`);
        }

        handlers.onOpen?.();
        backoffMs = INITIAL_BACKOFF_MS;

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buf = "";

        while (!signal.aborted) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });

          // SSE frames are delimited by a blank line (\n\n).
          let idx: number;
          while ((idx = buf.indexOf("\n\n")) >= 0) {
            const raw = buf.slice(0, idx);
            buf = buf.slice(idx + 2);
            const frame = parseFrame(raw);
            if (!frame || frame.data == null) continue;
            try {
              const parsed = JSON.parse(frame.data);
              if (frame.event === "snapshot") handlers.onSnapshot?.(parsed);
              else handlers.onEvent?.(frame.event, parsed);
            } catch {
              // Drop malformed frames — the next one usually arrives fine.
            }
          }
        }
      } catch (err) {
        if (signal.aborted) return;
        handlers.onError?.(err);
        await sleep(backoffMs);
        backoffMs = Math.min(backoffMs * 2, MAX_BACKOFF_MS);
      }
    }
  })();

  return () => ctrl.abort();
}

interface ParsedFrame {
  event: string;
  data: string | null;
}

/** Internal frame parser — exported only for unit tests. */
export function parseFrame(raw: string): ParsedFrame | null {
  if (!raw) return null;
  let event = "message";
  let data: string | null = null;
  for (const line of raw.split("\n")) {
    if (!line || line.startsWith(":")) continue;
    if (line.startsWith("event:")) event = line.slice(6).trim();
    else if (line.startsWith("data:")) {
      const chunk = line.slice(5).replace(/^ /, "");
      data = data == null ? chunk : `${data}\n${chunk}`;
    }
  }
  return { event, data };
}
